import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  updateConfig,
  saveConfigs,
  deleteConfig,
  readConfigs,
  addConfig,
  addConstraint,
  deleteConstraint,
  copyConfig,
} from '../../actions/configs';
import { runSimulation, invalidateSimulation } from '../../actions/simulations';
import { uploadImage } from '../../actions/images';
import { capitalize, stripWhiteSpaces } from '../../libs/text';
import Spinner from '../../components/spinner';
import ConfigurationForm from '../../components/configuration-form';
import { formatPercentage} from '../../libs/num';
import { validateTags } from '../../../../common/validation/tags';
import { validateConstraints } from '../../../../common/validation/constraints';
import style from './style.css';
import { checkConfigAndPromptIfError } from '../../utils/validate-config';
import { ErrorCode as ConfigErrorCode } from '../../../../common/validation/config';

class ConfigurationsEditor extends React.Component {
  constructor(props) {
    super(props);

    this.onSaveConfigs = this.onSaveConfigs.bind(this);
    this.onRevertConfigs = this.onRevertConfigs.bind(this);
    this.onConfigDragOver = this.onConfigDragOver.bind(this);
    this.onRunSimulation = this.onRunSimulation.bind(this);
  }

  render() {
    const { currentKey, configs, images, filters, simulations } = this.props;
    const config = configs.byKey[currentKey];

    if (!config || (!config && configs.isFetchingConfigs)) {
      return (
        <section className={ style.spinnerContainer }>
          <Spinner/>
        </section>
      );
    }

    const formFields = [
      'weight',
      'tag',
      'link',
      'image',
      'header',
      'body',
      'button',
    ];

    // Return configuration forms based on filter function that will be called
    // with an config object, typically filtered on the slot field.
    const filteredConfigurationForms = (filter) => {
      return config.items.reduce((acc, values, index) => {
        if (filter(values)) {
          const handlers = formFields.reduce((acc, field) => {
            acc[`on${capitalize(field)}Change`] = this.onConfigValueChange.bind(this, index, field);
            return acc;
          }, {});

          let simulatedPercentage;

          if (simulations.isRunning) {
            simulatedPercentage = '0%';
          } else if (simulations.byKey[currentKey]) {
            const portion = simulations.byKey[currentKey][values.tag] || 0;
            const total = simulations.byKey[currentKey].estimation;
            simulatedPercentage = formatPercentage(portion, total);
          }

          acc.push(
            <div
              key={ index }
              className={ style.draggable}
              draggable={ true }
              onDragStart={ this.onConfigDragStart.bind(this, index) }
            >
              <ConfigurationForm
                { ...handlers }
                { ...values }
                imageUrls={ images.urls }
                onFileSelect={ this.onFileSelect.bind(this, index) }
                onDeleteClick={ this.onDeleteConfigClick.bind(this, index) }
                onConstraintAdd={ this.onConstraintAdd.bind(this, index) }
                onConstraintDelete={ this.onConstraintDelete.bind(this, index) }
                isSaving={ config.isSaving }
                filters={ filters }
                simulatedPercentage={ simulatedPercentage }
                configKeys={ configs.allKeys }
                onConfigKeySelect={ this.onConfigKeyCopySelect.bind(this, index) }
              />
            </div>
          );
        }

        return acc;
      }, []);
    };

    const configsForFirstPos = filteredConfigurationForms(config =>  config.slot === 0);
    const configsForSecondPos = filteredConfigurationForms(config =>  config.slot === 1);

    return (
      <div>
        <section className={ style.slotContainer }>
          <div
            className={ style.slot }
            onDragOver={ this.onConfigDragOver }
            onDrop={ this.onConfigDrop.bind(this, 0) }
          >
            <h2 className={ style.slotTitle }>Första position</h2>
            { configsForFirstPos }
            <button
              className={ style.wideSecondaryButton}
              onClick={ this.onAddConfigClick.bind(this, 0)}
            >Lägg till annons</button>
          </div>
          <div
            className={ style.slot }
            onDragOver={ this.onConfigDragOver }
            onDrop={ this.onConfigDrop.bind(this, 1) }
          >
            <h2 className={ style.slotTitle }>Andra position</h2>
            { configsForSecondPos }
            <button
              className={ style.wideSecondaryButton}
              onClick={ this.onAddConfigClick.bind(this, 1)}
            >Lägg till annons</button>
          </div>
        </section>
        <button
          className={ style.primaryButton }
          disabled={ config.isSaved || config.isSaving }
          onClick={ this.onSaveConfigs }
        >Spara ändringar</button>
        <button
          className={ style.secondaryButton }
          disabled={ config.isSaved || config.isSaving }
          onClick={ this.onRevertConfigs }
        >Återgå till tidigare sparad</button>
        <button
          disabled={
            config.isSaving
            || validateTags(configs.byKey[currentKey]) !== null
            || validateConstraints(configs.byKey[currentKey], 2) !== null
          }
          className={ style.secondaryButton }
          onClick={ this.onRunSimulation }
        >Kör simulering</button>
      </div>
    );
  }

  onConfigValueChange(index, key, event) {
    const { dispatch, currentKey } = this.props;
    const { type } = event.target;
    let { value } = event.target;

    if (type === 'number' || type === 'range') {
      value = parseInt(value, 10);
      if (!value || value < 1 || value > 1000) {
        return;
      }
    }

    if (key === 'tag') {
      value = stripWhiteSpaces(value);
    }

    dispatch(invalidateSimulation(currentKey));
    dispatch(updateConfig(currentKey, index, { [key]: value }));
  }

  onSaveConfigs(/*event*/) {
    const { dispatch, currentKey, configs } = this.props;
    const config = configs.byKey[currentKey];
    const { sendDate } = config;

    if (currentKey === configs.activeKey) {
      if (!checkConfigAndPromptIfError(config, { [ConfigErrorCode.NOT_SAVED]: true } )) {
        return;
      }

      dispatch(saveConfigs(currentKey, configs.byKey[currentKey].items, sendDate ));
      return;
    }

    dispatch(saveConfigs(currentKey, configs.byKey[currentKey].items, sendDate ));
  }

  onRevertConfigs(/*event*/) {
    const { dispatch, currentKey } = this.props;
    dispatch(invalidateSimulation(currentKey));
    dispatch(readConfigs(currentKey));
  }

  onRunSimulation(/*event*/) {
    const { dispatch, currentKey } = this.props;
    dispatch(runSimulation(currentKey));
  }

  cancelEvent(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onConfigDragStart(index, event) {
    if (document.activeElement.tagName === 'INPUT') {
      // Block dragging on input elements, in particular range inputs
      this.cancelEvent(event);
      return false;
    }

    event.dataTransfer.setData('text/plain', index);
  }

  onConfigDragOver(event) {
    this.cancelEvent(event);
    event.dataTransfer.dropEffect = 'move';
  }

  onConfigDrop(slot, event) {
    this.cancelEvent(event);

    const { dispatch, currentKey } = this.props;
    const index = parseInt(event.dataTransfer.getData('text/plain'), 10);
    dispatch(invalidateSimulation(currentKey));
    dispatch(updateConfig(currentKey, index, { slot }));
  }

  onFileSelect(index, file) {
    const { dispatch, currentKey } = this.props;

    const formData = new FormData();
    formData.append('file', file);

    dispatch(uploadImage(currentKey, index, formData));
  }

  onAddConfigClick(slot) {
    const { currentKey, dispatch } = this.props;
    dispatch(invalidateSimulation(currentKey));
    dispatch(addConfig(currentKey, slot));
  }

  onDeleteConfigClick(index) {
    const { dispatch, currentKey } = this.props;
    dispatch(invalidateSimulation(currentKey));
    dispatch(deleteConfig(currentKey, index));
  }

  onConstraintAdd(index, row, constraint) {
    const { dispatch, currentKey } = this.props;
    dispatch(invalidateSimulation(currentKey));
    dispatch(addConstraint(currentKey, index, row, constraint));
  }

  onConstraintDelete(index, row, column) {
    const {dispatch, currentKey} = this.props;
    dispatch(invalidateSimulation(currentKey));
    dispatch(deleteConstraint(currentKey, index, row, column));
  }

  onConfigKeyCopySelect(index, event) {
    const { configs, currentKey, dispatch } = this.props;
    const targetKey = event.target.value;

    const sourceItem = configs.byKey[currentKey].items[index];

    if (!configs.byKey[targetKey]) {
      dispatch(readConfigs(targetKey))
        .then(() => {
          dispatch(copyConfig(targetKey, sourceItem));
        });
      return;
    }

    dispatch(invalidateSimulation(targetKey));
    dispatch(copyConfig(targetKey, sourceItem));
  }
}

ConfigurationsEditor.propTypes = {
  configs: PropTypes.object.isRequired,
  images: PropTypes.object.isRequired,
  currentKey: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  simulations: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
    images: state.images,
    filters: state.filters,
    simulations: state.simulations,
  };
};

export default connect(mapStateToProps)(ConfigurationsEditor);




// WEBPACK FOOTER //
// ./client/src/containers/configurations-editor/index.js